import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster } from "./ui/sonner";
import Header from "./Header";

const LOGO_SMALL = "/assets/logo_small.png";

// Rules Content Translations
const rulesContent = {
  it: {
    nav: {
      signIn: "Accedi",
      register: "Registrati",
      menu: {
        rules: "Regolamento",
        community: "Community",
        topPlayers: "Top Players",
        playerValues: "Prezzi Giocatori",
        download: "Scarica l'App",
        contact: "Contatti"
      }
    },
    footer: {
      rights: "Tutti i diritti riservati.",
      contact: "Domande o feedback?"
    },
    pageTitle: "REGOLAMENTO",
    readFullRules: "Leggi il regolamento completo",
    intro: {
      title: "Come funziona FANTEEX",
      p1: "Fanteex è un fantasy calcistico \"every day\": ogni matchday della stagione è un'occasione per metterti in gioco. Crea la tua rosa, investi i tuoi TEEX e sfida amici o l'intera community.",
      p2: "Non ci sono vincoli di moduli o reparti: scegli i giocatori che preferisci tra tutti quelli disponibili nelle competizioni della stagione. L'unico limite è il budget di 10 TEEX per la rosa della sfida.",
      p3: "I punti arrivano da eventi reali delle partite (minuti giocati, gol, assist, cartellini, porta inviolata, ecc.) e determinano il vincitore e la distribuzione dei TEEX, in tutte le modalità disponibili: Head 2 Head, Private League e Room Multiplayer.",
      inBrief: "In breve",
      bullets: [
        "Parti con 500 TEEX",
        "Budget rosa: max 10 TEEX",
        "Modalità: Head 2 Head / Private League / Room Multiplayer",
        "Punteggio: eventi reali delle partite della stagione + bonus squadra"
      ]
    },
    sections: {
      s1: {
        title: "1. Introduzione",
        content: "Benvenuto su FANTEEX! Non sei più spettatore: schiera la tua formazione, accumula TEEX e dimostra di essere il miglior talent scout del pianeta."
      },
      s2: {
        title: "2. Registrazione e TEEX",
        content: "Ogni utente parte con 500 TEEX. Quando costruisci la squadra per una sfida, spendi i tuoi TEEX per acquistare i giocatori — il tetto massimo è 10 TEEX totali per rosa. Il costo di ogni giocatore varia in base al suo valore, alla partita e alla squadra di appartenenza."
      },
      s3: {
        title: "3. Tipi di Sfida",
        items: [
          { label: "Head 2 Head", desc: "testa a testa. Chi fa più punti vince e si prende tutti i TEEX in palio." },
          { label: "Private League", desc: "fino a 10 partecipanti. Montepremi: 60% al 1°, 30% al 2°, 10% al 3°." },
          { label: "Room Multiplayer", desc: "entra nelle room pubbliche con altri utenti e vinci ancora più TEEX. Montepremi: 60% al 1°, 30% al 2°, 10% al 3°." }
        ]
      },
      s4: {
        title: "4. Creazione di una Sfida",
        intro: "Tre passaggi:",
        steps: [
          "Scegli la modalità: Head 2 Head, Private League o Room Multiplayer",
          "Crea la rosa selezionando i giocatori (spendendo fino a 10 TEEX)",
          "Conferma e invita gli amici — devono accettare dall'area \"Inviti\" oppure entra in una Room già aperta"
        ]
      },
      s5: {
        title: "5. Composizione della Squadra",
        content: "Scegli liberamente i giocatori reali in gara nelle competizioni della stagione: nessun vincolo di reparto, di squadra o numero minimo di elementi. L'unico limite è il budget di 10 TEEX. Ogni giocatore ha un costo variabile che riflette le aspettative sulla sua partita."
      },
      s6: {
        title: "6. Stati della Sfida",
        items: [
          { label: "Ready", desc: "le squadre sono confermate, si attende il fischio d'inizio" },
          { label: "Live", desc: "le partite sono in corso, i punti vengono aggiornati in tempo reale" },
          { label: "Completed", desc: "le partite sono terminate, i punti sono stati assegnati e puoi ritirare i tuoi TEEX virtuali" }
        ]
      },
      s7: {
        title: "7. Punteggio",
        intro: "I punti derivano da eventi reali delle partite della stagione, sia a livello individuale che di squadra. Ogni azione in campo ha un valore specifico:",
        tableHeaders: ["Categoria", "Evento", "Punti"],
        playerEvents: "Giocatori",
        teamEvents: "Squadra",
        playerRows: [
          ["Minuti giocati", "90 (titolare)", "+2"],
          ["Minuti giocati", "da 45 a 89", "+1.5"],
          ["Minuti giocati", "da 1 a 44", "+1"],
          ["Rigore parato", "", "+3"],
          ["Rigore sbagliato", "", "-3"],
          ["Cartellino giallo", "", "-0.5"],
          ["Cartellino rosso", "", "-1"],
          ["Portiere: porta inviolata", "", "+1"],
          ["Autogol", "", "-2"],
          ["Assist", "", "+1"],
          ["Gol segnato", "", "+3"],
          ["Gol subito (solo portiere)", "", "-1"]
        ],
        teamRows: [
          ["Gol subiti dalla squadra", "", "-0.5 per gol"],
          ["Gol segnati dalla squadra", "", "+0.5 per gol"],
          ["Vittoria", "", "+1"],
          ["Porta inviolata", "", "+0.5"]
        ]
      },
      s8: {
        title: "8. Vittoria e Montepremi",
        h2h: "Head 2 Head: chi fa più punti prende tutto il montepremi.",
        league: "Private League e Room Multiplayer:",
        prizes: ["1° classificato: 60% del montepremi", "2° classificato: 30% del montepremi", "3° classificato: 10% del montepremi"]
      },
      s9: {
        title: "9. Classifica Globale",
        content: "Accumula TEEX partita dopo partita, scala la classifica globale e migliora il tuo Teex Win Index. I migliori manager di Fanteex saranno visibili nella Top Players globale."
      },
      s10: {
        title: "10. Esempio di Punteggio",
        intro: "Ecco un esempio reale di calcolo punti per una partita della stagione:",
        playerName: "Jude Bellingham (Real Madrid)",
        breakdown: [
          ["Titolare (90 minuti)", "+2"],
          ["Gol segnato", "+3"],
          ["Vittoria squadra", "+1"],
          ["Gol segnati dalla squadra (2 gol × +0.5)", "+1"],
          ["Gol subiti dalla squadra (1 gol × -0.5)", "-0.5"]
        ],
        total: "Totale: 6.5 punti"
      },
      s11: {
        title: "11. Partite Posticipate",
        content: "Se una partita della stagione viene posticipata e non si conclude prima dell'apertura del prossimo Matchday:",
        rules: [
          "I punti totalizzati dai giocatori coinvolti nella partita posticipata saranno uguali al costo che hai pagato per selezionarli",
          "Quando il posticipo è ufficiale, quei giocatori non saranno selezionabili per le sfide dei Matchday successivi"
        ],
        note: "Regola pensata per garantire equità e trasparenza anche in caso di imprevisti nel calendario della competizione."
      }
    },
    disclaimer: "FANTEEX è una competizione basata su abilità, strategia e conoscenza del calcio: l'esito delle sfide dipende esclusivamente dai punti ottenuti attraverso le prestazioni reali dei giocatori nelle competizioni della stagione."
  },
  en: {
    nav: {
      signIn: "Sign In",
      register: "Register",
      menu: {
        rules: "Game Rules",
        community: "Community",
        topPlayers: "Top Players",
        playerValues: "Player Values",
        download: "Download App",
        contact: "Get in Touch"
      }
    },
    footer: {
      rights: "All rights reserved.",
      contact: "Questions or feedback?"
    },
    pageTitle: "GAME RULES",
    readFullRules: "Read the full rules",
    intro: {
      title: "How FANTEEX Works",
      p1: "Fanteex is an \"every day\" fantasy football game: every matchday of the season is an opportunity to play. Build your squad, invest your TEEX and challenge friends or the entire community.",
      p2: "There are no formation or role constraints: choose any player available across the season's competitions. The only limit is the 10 TEEX budget for your challenge squad.",
      p3: "Points come from real match events (minutes played, goals, assists, cards, clean sheets, etc.) and determine the winner and TEEX distribution across all available modes: Head 2 Head, Private League and Room Multiplayer.",
      inBrief: "In brief",
      bullets: [
        "Start with 500 TEEX",
        "Squad budget: max 10 TEEX",
        "Modes: Head 2 Head / Private League / Room Multiplayer",
        "Scoring: real match events of the season + team bonuses"
      ]
    },
    sections: {
      s1: {
        title: "1. Introduction",
        content: "Welcome to FANTEEX! You're no longer just a spectator: field your squad, accumulate TEEX and prove you're the best talent scout on the planet."
      },
      s2: {
        title: "2. Registration and TEEX",
        content: "Every user starts with 500 TEEX. When building your squad for a challenge, you spend TEEX to purchase players — the maximum cap is 10 TEEX total per squad. Each player's cost varies based on their value, the match, and their team."
      },
      s3: {
        title: "3. Challenge Types",
        items: [
          { label: "Head 2 Head", desc: "one-on-one. Whoever scores more points wins and takes all the TEEX at stake." },
          { label: "Private League", desc: "up to 10 participants. Prize pool: 60% to 1st, 30% to 2nd, 10% to 3rd." },
          { label: "Room Multiplayer", desc: "join public rooms with other users and win even more TEEX. Prize pool: 60% to 1st, 30% to 2nd, 10% to 3rd." }
        ]
      },
      s4: {
        title: "4. Creating a Challenge",
        intro: "Three steps:",
        steps: [
          "Choose your mode: Head 2 Head, Private League or Room Multiplayer",
          "Build your squad by selecting players (spending up to 10 TEEX)",
          "Confirm and invite friends — they must accept from the \"Invites\" area, or join an already open Room"
        ]
      },
      s5: {
        title: "5. Squad Composition",
        content: "Freely choose real players competing in the season's competitions: no role constraints, team restrictions, or minimum player counts. The only limit is the 10 TEEX budget. Each player has a variable cost reflecting expectations for their match."
      },
      s6: {
        title: "6. Challenge States",
        items: [
          { label: "Ready", desc: "squads confirmed, waiting for kickoff" },
          { label: "Live", desc: "matches in progress, points updated in real time" },
          { label: "Completed", desc: "matches finished, points assigned, you can withdraw your virtual TEEX" }
        ]
      },
      s7: {
        title: "7. Scoring",
        intro: "Points are derived from real match events of the season, both at individual and team level. Each on-field action has a specific value:",
        tableHeaders: ["Category", "Event", "Points"],
        playerEvents: "Players",
        teamEvents: "Team",
        playerRows: [
          ["Minutes played", "90 (starter)", "+2"],
          ["Minutes played", "45 to 89", "+1.5"],
          ["Minutes played", "1 to 44", "+1"],
          ["Penalty saved", "", "+3"],
          ["Penalty missed", "", "-3"],
          ["Yellow card", "", "-0.5"],
          ["Red card", "", "-1"],
          ["Goalkeeper: clean sheet", "", "+1"],
          ["Own goal", "", "-2"],
          ["Assist", "", "+1"],
          ["Goal scored", "", "+3"],
          ["Goal conceded (goalkeeper only)", "", "-1"]
        ],
        teamRows: [
          ["Goals conceded by team", "", "-0.5 per goal"],
          ["Goals scored by team", "", "+0.5 per goal"],
          ["Win", "", "+1"],
          ["Clean sheet", "", "+0.5"]
        ]
      },
      s8: {
        title: "8. Winning and Prize Pool",
        h2h: "Head 2 Head: whoever scores more points takes the entire prize pool.",
        league: "Private League and Room Multiplayer:",
        prizes: ["1st place: 60% of prize pool", "2nd place: 30% of prize pool", "3rd place: 10% of prize pool"]
      },
      s9: {
        title: "9. Global Leaderboard",
        content: "Accumulate TEEX match after match, climb the global rankings and improve your Teex Win Index. The best Fanteex managers will be featured in the global Top Players."
      },
      s10: {
        title: "10. Scoring Example",
        intro: "Here's a real example of point calculation for a match of the season:",
        playerName: "Jude Bellingham (Real Madrid)",
        breakdown: [
          ["Starter (90 minutes)", "+2"],
          ["Goal scored", "+3"],
          ["Team win", "+1"],
          ["Team goals scored (2 goals × +0.5)", "+1"],
          ["Team goals conceded (1 goal × -0.5)", "-0.5"]
        ],
        total: "Total: 6.5 points"
      },
      s11: {
        title: "11. Postponed Matches",
        content: "If a match of the season is postponed and doesn't conclude before the next Matchday opens:",
        rules: [
          "Points for players involved in the postponed match will equal the cost paid to select them",
          "Once the postponement is official, those players won't be selectable for subsequent Matchday challenges"
        ],
        note: "This rule ensures fairness and transparency even when unexpected changes occur in the competition calendar."
      }
    },
    disclaimer: "FANTEEX is a competition based on skill, strategy and football knowledge: the outcome of challenges depends exclusively on points earned through real player performances in the season's competitions."
  }
};

// Tech Background Component
const TechBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
    <div className="absolute top-[-20%] left-[-20%] w-[80vw] h-[80vw] bg-[#1a0b2e] rounded-full blur-[100px] opacity-80" />
    <div className="absolute bottom-[-20%] right-[-20%] w-[80vw] h-[80vw] bg-[#0f0518] rounded-full blur-[100px] opacity-90" />
    <div className="absolute inset-0 bg-[linear-gradient(rgba(93,253,203,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(93,253,203,0.03)_1px,transparent_1px)] bg-[size:100px_100px]" />
  </div>
);

const RulesPage = () => {
  const [lang, setLang] = useState('it');
  const t = rulesContent[lang];

  const toggleLanguage = () => {
    setLang(prev => prev === 'it' ? 'en' : 'it');
  };

  const scrollToRules = () => {
    document.getElementById('rules-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="relative min-h-screen bg-fanteex-dark overflow-x-hidden flex flex-col font-sans selection:bg-[#5DFDCB] selection:text-fanteex-dark">
      {/* Background */}
      <div className="fixed inset-0 z-0 pointer-events-none bg-[#0f0518]">
        <div className="absolute inset-0 opacity-15 mix-blend-overlay" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/noise.svg)` }}></div>
      </div>

      <TechBackground />

      {/* Navigation */}
      <Header t={t} lang={lang} toggleLanguage={toggleLanguage} />

      {/* Page Content */}
      <main className="flex-1 pt-32 pb-20 px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Page Title */}
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-5xl md:text-7xl font-display font-black text-white uppercase mb-8 text-center"
          >
            {t.pageTitle}
          </motion.h1>

          {/* Quick Link to Full Rules */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-center mb-12"
          >
            <button
              onClick={scrollToRules}
              className="text-[#5DFDCB] hover:text-white transition-colors underline underline-offset-4 font-display font-bold uppercase text-sm tracking-wider"
            >
              {t.readFullRules} ↓
            </button>
          </motion.div>

          {/* INTRO SECTION */}
          <motion.section
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-black/40 backdrop-blur-xl border border-[#5DFDCB]/20 rounded-2xl p-8 md:p-12 mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-display font-black text-[#5DFDCB] uppercase mb-6">
              {t.intro.title}
            </h2>

            <div className="space-y-4 text-white/80 font-sans text-base md:text-lg leading-relaxed">
              <p>{t.intro.p1}</p>
              <p>{t.intro.p2}</p>
              <p>{t.intro.p3}</p>
            </div>

            {/* In Brief Box */}
            <div className="mt-8 bg-[#5DFDCB]/10 border border-[#5DFDCB]/30 rounded-xl p-6">
              <h3 className="text-lg font-display font-black text-[#5DFDCB] uppercase mb-4">
                {t.intro.inBrief}
              </h3>
              <ul className="space-y-2">
                {t.intro.bullets.map((bullet, index) => (
                  <li key={index} className="flex items-start gap-3 text-white/90 font-sans">
                    <span className="text-[#5DFDCB] mt-1">•</span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.section>

          {/* FULL RULES SECTION */}
          <motion.section
            id="rules-section"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-black/40 backdrop-blur-xl border border-[#5DFDCB]/20 rounded-2xl p-8 md:p-12 space-y-10"
          >
            {/* Section 1 */}
            <div>
              <h3 className="text-xl md:text-2xl font-display font-black text-white uppercase mb-4 border-b border-[#5DFDCB]/20 pb-2">
                {t.sections.s1.title}
              </h3>
              <p className="text-white/80 font-sans">{t.sections.s1.content}</p>
            </div>

            {/* Section 2 */}
            <div>
              <h3 className="text-xl md:text-2xl font-display font-black text-white uppercase mb-4 border-b border-[#5DFDCB]/20 pb-2">
                {t.sections.s2.title}
              </h3>
              <p className="text-white/80 font-sans">{t.sections.s2.content}</p>
            </div>

            {/* Section 3 */}
            <div>
              <h3 className="text-xl md:text-2xl font-display font-black text-white uppercase mb-4 border-b border-[#5DFDCB]/20 pb-2">
                {t.sections.s3.title}
              </h3>
              <ul className="space-y-3">
                {t.sections.s3.items.map((item, index) => (
                  <li key={index} className="text-white/80 font-sans">
                    <span className="text-[#5DFDCB] font-bold">{item.label}:</span> {item.desc}
                  </li>
                ))}
              </ul>
            </div>

            {/* Section 4 */}
            <div>
              <h3 className="text-xl md:text-2xl font-display font-black text-white uppercase mb-4 border-b border-[#5DFDCB]/20 pb-2">
                {t.sections.s4.title}
              </h3>
              <p className="text-white/80 font-sans mb-3">{t.sections.s4.intro}</p>
              <ol className="list-decimal list-inside space-y-2 text-white/80 font-sans pl-2">
                {t.sections.s4.steps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            </div>

            {/* Section 5 */}
            <div>
              <h3 className="text-xl md:text-2xl font-display font-black text-white uppercase mb-4 border-b border-[#5DFDCB]/20 pb-2">
                {t.sections.s5.title}
              </h3>
              <p className="text-white/80 font-sans">{t.sections.s5.content}</p>
            </div>

            {/* Section 6 */}
            <div>
              <h3 className="text-xl md:text-2xl font-display font-black text-white uppercase mb-4 border-b border-[#5DFDCB]/20 pb-2">
                {t.sections.s6.title}
              </h3>
              <ul className="space-y-3">
                {t.sections.s6.items.map((item, index) => (
                  <li key={index} className="text-white/80 font-sans">
                    <span className="text-[#5DFDCB] font-bold">{item.label}:</span> {item.desc}
                  </li>
                ))}
              </ul>
            </div>

            {/* Section 7 - Scoring Table */}
            <div>
              <h3 className="text-xl md:text-2xl font-display font-black text-white uppercase mb-4 border-b border-[#5DFDCB]/20 pb-2">
                {t.sections.s7.title}
              </h3>
              <p className="text-white/80 font-sans mb-6">{t.sections.s7.intro}</p>

              {/* Responsive Table Wrapper */}
              <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
                <table className="w-full min-w-[400px] border-collapse">
                  <thead className="sticky top-0">
                    <tr className="bg-[#5DFDCB]/20">
                      {t.sections.s7.tableHeaders.map((header, index) => (
                        <th
                          key={index}
                          className="text-left text-[#5DFDCB] font-display font-bold uppercase text-sm p-3 border border-[#5DFDCB]/30"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Player Events Header */}
                    <tr className="bg-[#5DFDCB]/10">
                      <td colSpan="3" className="text-[#5DFDCB] font-display font-bold uppercase text-sm p-3 border border-[#5DFDCB]/30">
                        {t.sections.s7.playerEvents}
                      </td>
                    </tr>
                    {t.sections.s7.playerRows.map((row, index) => (
                      <tr key={`player-${index}`} className={index % 2 === 0 ? 'bg-white/5' : 'bg-transparent'}>
                        <td className="text-white/80 font-sans p-3 border border-[#5DFDCB]/20">{row[0]}</td>
                        <td className="text-white/60 font-sans p-3 border border-[#5DFDCB]/20">{row[1]}</td>
                        <td className={`font-sans font-bold p-3 border border-[#5DFDCB]/20 ${row[2].startsWith('+') ? 'text-[#5DFDCB]' : 'text-red-400'}`}>
                          {row[2]}
                        </td>
                      </tr>
                    ))}
                    {/* Team Events Header */}
                    <tr className="bg-[#5DFDCB]/10">
                      <td colSpan="3" className="text-[#5DFDCB] font-display font-bold uppercase text-sm p-3 border border-[#5DFDCB]/30">
                        {t.sections.s7.teamEvents}
                      </td>
                    </tr>
                    {t.sections.s7.teamRows.map((row, index) => (
                      <tr key={`team-${index}`} className={index % 2 === 0 ? 'bg-white/5' : 'bg-transparent'}>
                        <td className="text-white/80 font-sans p-3 border border-[#5DFDCB]/20">{row[0]}</td>
                        <td className="text-white/60 font-sans p-3 border border-[#5DFDCB]/20">{row[1]}</td>
                        <td className={`font-sans font-bold p-3 border border-[#5DFDCB]/20 ${row[2].startsWith('+') ? 'text-[#5DFDCB]' : 'text-red-400'}`}>
                          {row[2]}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Section 8 */}
            <div>
              <h3 className="text-xl md:text-2xl font-display font-black text-white uppercase mb-4 border-b border-[#5DFDCB]/20 pb-2">
                {t.sections.s8.title}
              </h3>
              <p className="text-white/80 font-sans mb-3">{t.sections.s8.h2h}</p>
              <p className="text-white/80 font-sans mb-2">{t.sections.s8.league}</p>
              <ul className="space-y-1 pl-4">
                {t.sections.s8.prizes.map((prize, index) => (
                  <li key={index} className="text-[#5DFDCB] font-sans font-bold">• {prize}</li>
                ))}
              </ul>
            </div>

            {/* Section 9 */}
            <div>
              <h3 className="text-xl md:text-2xl font-display font-black text-white uppercase mb-4 border-b border-[#5DFDCB]/20 pb-2">
                {t.sections.s9.title}
              </h3>
              <p className="text-white/80 font-sans">{t.sections.s9.content}</p>
            </div>

            {/* Section 10 - Example */}
            <div>
              <h3 className="text-xl md:text-2xl font-display font-black text-white uppercase mb-4 border-b border-[#5DFDCB]/20 pb-2">
                {t.sections.s10.title}
              </h3>
              <p className="text-white/80 font-sans mb-4">{t.sections.s10.intro}</p>

              <div className="bg-[#5DFDCB]/10 border border-[#5DFDCB]/30 rounded-xl p-6">
                <p className="text-[#5DFDCB] font-display font-bold text-lg mb-4">{t.sections.s10.playerName}</p>
                <ul className="space-y-2">
                  {t.sections.s10.breakdown.map((item, index) => (
                    <li key={index} className="flex justify-between text-white/80 font-sans border-b border-white/10 pb-2">
                      <span>{item[0]}</span>
                      <span className={`font-bold ${item[1].startsWith('+') ? 'text-[#5DFDCB]' : 'text-red-400'}`}>{item[1]}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-white font-display font-black text-xl mt-4 pt-2 border-t border-[#5DFDCB]/30">
                  {t.sections.s10.total}
                </p>
              </div>
            </div>

            {/* Section 11 */}
            <div>
              <h3 className="text-xl md:text-2xl font-display font-black text-white uppercase mb-4 border-b border-[#5DFDCB]/20 pb-2">
                {t.sections.s11.title}
              </h3>
              <p className="text-white/80 font-sans mb-3">{t.sections.s11.content}</p>
              <ol className="list-decimal list-inside space-y-2 text-white/80 font-sans pl-2 mb-4">
                {t.sections.s11.rules.map((rule, index) => (
                  <li key={index}>{rule}</li>
                ))}
              </ol>
              <p className="text-white/60 font-sans italic">{t.sections.s11.note}</p>
            </div>

            {/* Disclaimer */}
            <div className="mt-12 pt-8 border-t border-[#5DFDCB]/20">
              <p className="text-white/60 font-sans text-sm italic text-center">
                {t.disclaimer}
              </p>
            </div>
          </motion.section>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full py-12 border-t border-white/5 bg-black text-center relative z-20">
        <Link to="/">
          <img src={LOGO_SMALL} alt="Fanteex" className="h-8 w-auto mx-auto mb-4 opacity-50 grayscale hover:grayscale-0 transition-all" />
        </Link>
        <p className="text-fanteex-textMuted/50 text-xs font-sans uppercase tracking-widest mb-2">
          &copy; 2026 Fanteex. {t.footer.rights}
        </p>
        <p className="text-fanteex-textMuted/30 text-xs font-sans">
          {t.footer.contact}{" "}
          <a href="mailto:info@fanteex.com" className="text-[#5DFDCB] hover:underline">
            info@fanteex.com
          </a>
        </p>
      </footer>

      <Toaster position="bottom-center" theme="dark" />
    </div>
  );
};

export default RulesPage;
